import { useEffect, useState } from "react";
import type { Guide, GuideCategory, GuideFormData } from "../../types/guides";
import {
  downloadGuideAsMarkdown,
  importGuideFromFile,
} from "../../utils/guide-export";
import { Icon, Label, Modal, Select } from "../atoms";
import Button from "../atoms/Button";
import IconButton from "../atoms/IconButton";
import {
  FormField,
  MarkdownEditor,
  ModalFooter,
  ModalHeader,
  TagInput,
} from "../molecules";

// Local Guide interface for compatibility with local storage service
interface LocalGuide {
  id: string;
  name: string;
  title: string;
  description: string;
  content: string;
  category: string;
  categoryId: string | null;
  tags: string[];
  version: number;
  createdAt: Date;
  updatedAt: Date;
}

// Union type to handle both API and local storage guides
type GuideUnion = Guide | LocalGuide;

// Type guard to check if guide is from API
function isApiGuide(guide: GuideUnion): guide is Guide {
  return "metadata" in guide;
}

// Helper to normalize guide data for form
function normalizeGuideForForm(guide: GuideUnion): {
  name: string;
  title: string;
  description: string;
  content: string;
  category: string;
  tags: string[];
} {
  if (isApiGuide(guide)) {
    return {
      name: guide.metadata.name,
      title: guide.metadata.title,
      description: guide.metadata.description,
      content: guide.content,
      category: guide.metadata.category,
      tags: guide.metadata.tags,
    };
  }
  return {
    name: guide.name,
    title: guide.title,
    description: guide.description,
    content: guide.content,
    category: guide.category,
    tags: guide.tags,
  };
}

interface GuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: GuideFormData) => void;
  onDelete?: (guideId: string, guideTitle: string) => void;
  guide?: GuideUnion | null;
  categories: GuideCategory[];
}

export function GuideModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  guide,
  categories,
}: GuideModalProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState<GuideFormData>({
    name: "",
    title: "",
    description: "",
    content: "",
    tags: [],
    category: "",
  });
  const [importError, setImportError] = useState<string | null>(null);

  useEffect(() => {
    if (guide) {
      const normalized = normalizeGuideForForm(guide);
      setFormData({
        name: normalized.name,
        title: normalized.title,
        description: normalized.description,
        content: normalized.content,
        tags: normalized.tags,
        category: normalized.category,
      });
    } else {
      setFormData({
        name: "",
        title: "",
        description: "",
        content: "",
        tags: [],
        category: "",
      });
    }
  }, [guide, isOpen]);

  const handleSave = () => {
    if (
      !formData.name.trim() ||
      !formData.title.trim() ||
      !formData.content.trim()
    ) {
      return;
    }
    onSave(formData);
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleDelete = () => {
    if (guide && onDelete) {
      if (showDeleteConfirm) {
        const id = isApiGuide(guide) ? guide.metadata.name : guide.id;
        const title = isApiGuide(guide) ? guide.metadata.title : guide.title;
        onDelete(id, title);
        onClose();
        setShowDeleteConfirm(false);
      } else {
        setShowDeleteConfirm(true);
      }
    }
  };

  const handleTagsChange = (newTags: string[]) => {
    setFormData((prev) => ({ ...prev, tags: newTags }));
  };

  const handleImportFile = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if file is markdown
    if (!file.name.endsWith(".md") && !file.name.endsWith(".markdown")) {
      setImportError("Please select a Markdown file (.md or .markdown)");
      return;
    }

    try {
      setImportError(null);
      const importedData = await importGuideFromFile(file);

      // Merge imported data with current form data
      setFormData((prev) => ({
        ...prev,
        ...importedData,
        // Preserve name if editing existing guide
        name: guide ? prev.name : importedData.name || prev.name,
        // Use imported tags or keep existing ones
        tags: importedData.tags || prev.tags,
        // Use imported category or keep existing one
        category: importedData.category || prev.category,
      }));
    } catch (error) {
      setImportError("Failed to import file. Please check the file format.");
    }

    // Reset the input so the same file can be imported again
    event.target.value = "";
  };

  // Convert categories to select options
  const categoryOptions = categories.map((category) => ({
    value: category.name,
    label: category.displayName,
    icon: category.icon ?? "📁",
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      closeOnEscape
      closeOnBackdrop
      className="h-[90vh] max-w-6xl flex flex-col"
    >
      <ModalHeader
        title={guide ? "Edit Guide" : "Create New Guide"}
        subtitle="Create and manage markdown guides for the coach"
        onClose={onClose}
        actions={
          <div className="flex items-center space-x-2">
            {/* Import button - always shown */}
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".md,.markdown"
                onChange={handleImportFile}
                className="hidden"
                id="import-guide-file"
              />
              <div className="inline-flex items-center justify-center p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-bg-secondary transition-colors" title="Import from Markdown">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12"
                  />
                </svg>
              </div>
            </label>

            {/* Export button - only shown when editing */}
            {guide && (
              <IconButton
                onClick={() => {
                  if (isApiGuide(guide)) {
                    downloadGuideAsMarkdown(guide);
                  } else {
                    // Convert LocalGuide to Guide format for export
                    const guideForExport: Guide = {
                      metadata: {
                        name: guide.name,
                        title: guide.title,
                        description: guide.description,
                        category: guide.category,
                        tags: guide.tags,
                        version: guide.version.toString(),
                        lastUpdated: guide.updatedAt.toISOString(),
                      },
                      content: guide.content,
                    };
                    downloadGuideAsMarkdown(guideForExport);
                  }
                }}
                variant="ghost"
                size="medium"
                title="Export as Markdown"
                aria-label="Export guide as Markdown"
                icon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                }
              />
            )}

            {/* Delete button - only shown when editing and onDelete is provided */}
            {guide && onDelete && (
              <IconButton
                onClick={handleDelete}
                variant={showDeleteConfirm ? "neutral" : "ghost"}
                size="medium"
                title="Delete guide"
                aria-label="Delete guide"
                icon={<Icon name="delete" size="sm" />}
              />
            )}
          </div>
        }
      />

      {/* Form Content - Side by Side Layout */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left Panel - Form Fields */}
        <div className="w-1/2 border-r border-border-light flex flex-col">
          <div className="p-6 overflow-y-auto flex-1">
            <div className="space-y-4">
              {/* Import error message */}
              {importError && (
                <div className="p-3 bg-danger-light border border-danger-border rounded-lg">
                  <p className="text-sm text-danger-text">{importError}</p>
                </div>
              )}

              <FormField
                id="guide-name"
                label="Name"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Enter guide name"
              />

              <FormField
                id="guide-title"
                label="Title"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="Enter guide title"
              />

              <div>
                <Label htmlFor="guide-category" required={false}>
                  Category
                </Label>
                <Select
                  id="guide-category"
                  value={formData.category ?? ""}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, category: value }))
                  }
                  options={[
                    { value: "", label: "Select category" },
                    ...categoryOptions,
                  ]}
                  placeholder="Select category"
                />
              </div>

              <FormField
                id="guide-description"
                label="Description"
                as="textarea"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Brief description of the guide"
                autoResize={false}
                minHeight={80}
              />

              <div>
                <Label htmlFor="guide-tags">Tags</Label>
                <TagInput
                  id="guide-tags"
                  tags={formData.tags}
                  onTagsChange={handleTagsChange}
                  placeholder="Type a tag and press Enter or comma to add"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Content Editor */}
        <div className="w-1/2 flex flex-col">
          <MarkdownEditor
            value={formData.content}
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, content: value }))
            }
            height={400}
            className="flex-1"
          />
        </div>
      </div>

      {/* Footer */}
      <ModalFooter className="flex-shrink-0">
        <div className="text-sm text-text-muted">
          {guide &&
            (isApiGuide(guide)
              ? `Version ${guide.metadata.version ?? "1.0.0"} • Last updated ${guide.metadata.lastUpdated ? new Date(guide.metadata.lastUpdated).toLocaleDateString() : "Unknown"}`
              : `Version ${guide.version} • Last updated ${guide.updatedAt.toLocaleDateString()}`)}
        </div>
        <div className="flex items-center space-x-3">
          <Button onClick={onClose} variant="neutral" size="medium">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="primary" size="medium">
            {guide ? "Update Guide" : "Create Guide"}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
